import {Card, DataStatus} from 'platform/components';
import {Grid, Hide, Show, Space} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {NewOfferPurchaseVehicleResponseBody} from '@dms/api/finance';
import {useGetPurchaseBusinessCasePricesOverviewQuery} from '@dms/api/sales';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {usePricePermissions} from '@dms/shared';
import {PriceBox} from '@dms/teas';

type BusinessCasePurchaseVehiclePriceSummaryProps = {
  purchaseVehicle: NewOfferPurchaseVehicleResponseBody;
  canReadCosts?: boolean;
  businessCaseId: string;
};

export const BusinessCasePurchaseVehiclePriceSummary = ({
  purchaseVehicle,
  canReadCosts,
  businessCaseId,
}: BusinessCasePurchaseVehiclePriceSummaryProps) => {
  const {
    data: purchaseVehiclePrices,
    isLoading,
    isError,
  } = useGetPurchaseBusinessCasePricesOverviewQuery({businessCaseId});

  const {canReadVehiclePurchasePrice, canReadVehicleSellingPrice, canReadVehicleEarnings} =
    usePricePermissions({
      vehicleRecordId: purchaseVehicle.vehicleId,
      businessCaseRecordId: null,
    });

  const {data: saleVehicle} = useGetSaleVehicleQuery(
    {vehicleId: purchaseVehicle?.vehicleId ?? ''},
    {skip: isNilOrEmpty(purchaseVehicle?.vehicleId)}
  );

  return (
    <Card
      variant="inlineGrey"
      isExpandable
      title={i18n.t`page.businessCase.labels.salVehiclePriceSummary`}
    >
      <DataStatus
        isEmpty={isNilOrEmpty(purchaseVehiclePrices)}
        isLoading={isLoading}
        isError={isError}
        minHeight={10}
      >
        <Grid columns={[1, 2, 4]}>
          <Show when={canReadVehiclePurchasePrice}>
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-buyingPrice')}
              align="left"
              price={purchaseVehiclePrices?.vehiclePurchasePrice?.withVat}
              priceSub={purchaseVehiclePrices?.vehiclePurchasePrice?.withoutVat}
              isVatDeductible={purchaseVehicle?.buying?.vatDeductible}
              title={i18n.t('entity.businessCase.labels.vehicleBuyingPrice')}
              foreignPrice={saleVehicle?.purchasePriceIsForeignCurrency}
            />
          </Show>
          <Show when={canReadCosts}>
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-costs')}
              align="left"
              price={purchaseVehiclePrices?.additionalCosts}
              title={i18n.t('entity.businessCase.labels.additionalExpectedCosts')}
              tooltipText={i18n.t('entity.businessCase.labels.additionalExpectedCostsTooltip')}
            />
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-totalExpectedCosts')}
              align="left"
              price={purchaseVehiclePrices?.totalCosts}
              title={i18n.t('entity.businessCase.labels.totalExpectedCosts')}
            />
            <Show when={canReadVehicleSellingPrice && canReadVehiclePurchasePrice}>
              <PriceBox
                data-testid={testIds.businessCase.buying('summary-vehicleGrossMargin')}
                colored
                align="left"
                percentTag={Number(purchaseVehiclePrices?.vehicleMargin?.percentage)}
                price={purchaseVehiclePrices?.vehicleMargin?.amount}
                title={i18n.t('entity.businessCase.labels.vehicleGrossMargin')}
                tooltipText={i18n.t('entity.businessCase.labels.vehicleGrossMarginTooltip')}
              />
            </Show>
            <Hide when={canReadVehicleSellingPrice && canReadVehiclePurchasePrice}>
              <Space fillAvailable />
            </Hide>
          </Show>
          <Hide when={canReadCosts}>
            <Space fillAvailable />
            <Space fillAvailable />
            <Space fillAvailable />
          </Hide>

          <Show when={canReadVehicleSellingPrice && canReadVehiclePurchasePrice}>
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-vehicleSellingPrice')}
              align="left"
              price={purchaseVehiclePrices?.vehicleSellingPrice?.withVat}
              priceSub={purchaseVehiclePrices?.vehicleSellingPrice?.withoutVat}
              isVatDeductible={purchaseVehicle?.buying?.vatDeductible}
              title={i18n.t('entity.businessCase.labels.vehicleSellingPrice')}
            />
          </Show>
          <Show when={canReadVehicleSellingPrice && canReadVehicleEarnings}>
            <Show when={canReadVehiclePurchasePrice}>
              <PriceBox
                data-testid={testIds.businessCase.buying('summary-expectedEarnings')}
                align="left"
                price={purchaseVehiclePrices?.additionalEarnings}
                title={i18n.t('entity.businessCase.labels.expectedEarnings')}
                tooltipText={i18n.t('entity.businessCase.labels.expectedEarningsTooltip')}
              />
            </Show>
            <Hide when={canReadVehiclePurchasePrice}>
              <Space fillAvailable />
            </Hide>
            <PriceBox
              data-testid={testIds.businessCase.buying('summary-realEarnings')}
              align="left"
              price={purchaseVehiclePrices?.totalEarnings}
              title={i18n.t('entity.businessCase.labels.totalExpectedEarnings')}
            />
            <Show when={canReadVehiclePurchasePrice}>
              <PriceBox
                data-testid={testIds.businessCase.buying('summary-totalGrossMargin')}
                colored
                align="left"
                percentTag={Number(purchaseVehiclePrices?.totalMargin?.percentage)}
                price={purchaseVehiclePrices?.totalMargin?.amount}
                title={i18n.t('entity.businessCase.labels.totalGrossMargin')}
                tooltipText={i18n.t('entity.businessCase.labels.totalGrossMarginTooltip')}
              />
            </Show>
          </Show>
          <Hide when={canReadVehicleSellingPrice}>
            <Space fillAvailable />
            <Space fillAvailable />
            <Space fillAvailable />
          </Hide>
        </Grid>
      </DataStatus>
    </Card>
  );
};
